import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";

interface Settings {
  windowDays: number;
  lateEnabled: boolean;
  policyText: string | null;
}

async function fetchSettings(): Promise<Settings> {
  const { data, error } = await supabase
    .from("app_settings")
    .select("key, value")
    .in("key", [
      "ticket_cancellation_window_days",
      "ticket_late_cancel_enabled",
      "ticket_cancellation_policy_text",
    ]);
  if (error) throw error;
  const byKey = new Map<string, unknown>((data || []).map((r: any) => [r.key, r.value]));
  const readNum = (v: unknown, fallback: number) => {
    if (typeof v === "number") return v;
    if (typeof v === "string") {
      const n = Number(v);
      return Number.isFinite(n) ? n : fallback;
    }
    if (v && typeof v === "object" && !Array.isArray(v)) {
      const days = (v as { days?: unknown }).days;
      if (typeof days === "number") return days;
      if (typeof days === "string") {
        const n = Number(days);
        return Number.isFinite(n) ? n : fallback;
      }
    }
    return fallback;
  };
  const readBool = (v: unknown, fallback: boolean) => {
    if (typeof v === "boolean") return v;
    if (typeof v === "string") return v === "true";
    if (v && typeof v === "object" && !Array.isArray(v)) {
      const enabled = (v as { enabled?: unknown }).enabled;
      if (typeof enabled === "boolean") return enabled;
      if (typeof enabled === "string") return enabled === "true";
    }
    return fallback;
  };
  const readText = (v: unknown): string | null => {
    if (v == null) return null;
    if (typeof v === "string") return v.length ? v : null;
    if (v && typeof v === "object" && !Array.isArray(v)) {
      const text = (v as { text?: unknown }).text;
      return typeof text === "string" && text.length ? text : null;
    }
    return null;
  };
  return {
    windowDays: readNum(byKey.get("ticket_cancellation_window_days"), 7),
    lateEnabled: readBool(byKey.get("ticket_late_cancel_enabled"), true),
    policyText: readText(byKey.get("ticket_cancellation_policy_text")),
  };
}

export interface CancellationPolicy {
  windowDays: number;
  lateEnabled: boolean;
  policyText: string | null;
  deadline: Date | null;
  isOutsideWindow: boolean;
  canSelfCancel: boolean;
  refundBranch: "refund" | "late" | "blocked";
  displayText: string;
  isLoading: boolean;
}

export function useCancellationPolicy(eventStartTime?: string | Date | null): CancellationPolicy {
  const { data, isLoading } = useQuery({
    queryKey: ["cancellation-policy-settings"],
    queryFn: fetchSettings,
    staleTime: 5 * 60 * 1000,
  });

  const settings: Settings = data ?? { windowDays: 7, lateEnabled: true, policyText: null };
  const start = eventStartTime ? new Date(eventStartTime) : null;
  const deadline =
    start ? new Date(start.getTime() - settings.windowDays * 24 * 60 * 60 * 1000) : null;
  const now = new Date();
  const isPast = start ? start.getTime() <= now.getTime() : false;
  const isOutsideWindow = deadline ? now.getTime() < deadline.getTime() : false;

  let refundBranch: "refund" | "late" | "blocked";
  if (isPast) refundBranch = "blocked";
  else if (isOutsideWindow) refundBranch = "refund";
  else refundBranch = settings.lateEnabled ? "late" : "blocked";

  const canSelfCancel = !isPast && (isOutsideWindow || settings.lateEnabled);

  const defaultText = deadline
    ? `Cancel by ${format(deadline, "EEE, d MMM 'at' HH:mm")} for a full credit refund. ` +
      (settings.lateEnabled
        ? "Cancellations after that release your seat but are non-refundable."
        : "Cancellations after that aren't available — please contact us if you need help.")
    : `Cancel up to ${settings.windowDays} days before the event for a full credit refund.`;

  return {
    windowDays: settings.windowDays,
    lateEnabled: settings.lateEnabled,
    policyText: settings.policyText,
    deadline,
    isOutsideWindow,
    canSelfCancel,
    refundBranch,
    displayText: settings.policyText ?? defaultText,
    isLoading,
  };
}
